import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  apiErrorCode,
  getCharityDonations,
  getCharityYearDetail,
  getDonationYears,
  getDonationYearSummary,
  getDonorTaxProfile,
  getParticipatingMerchants,
  saveDonorIcNumber
} from '../../api/donation.api';
import {
  DONATION_ERROR_CODES,
  type DonorTaxProfile,
  type SaveIcNumberPayload
} from '../../types/donation.types';

const DONATIONS_QUERY_KEY = ['donations'] as const;
const DONOR_TAX_PROFILE_QUERY_KEY = ['donorTaxProfile'] as const;

/** Save refusals that mean the profile on screen is out of date. */
const STALE_PROFILE_CODES: ReadonlySet<string> = new Set([
  DONATION_ERROR_CODES.DONOR_ID_EXISTS,
  DONATION_ERROR_CODES.DONOR_ID_NOT_APPLICABLE
]);

export const useDonationYears = () =>
  useQuery({
    queryKey: [...DONATIONS_QUERY_KEY, 'years'],
    queryFn: getDonationYears
  });

/**
 * Merchants to show a donor with no donations yet. Only an example, so a failure is never
 * retried or shown: the strip is left out. The set changes daily, so an hour's cache is enough.
 */
export const useParticipatingMerchants = () =>
  useQuery({
    queryKey: [...DONATIONS_QUERY_KEY, 'participating-merchants'],
    queryFn: getParticipatingMerchants,
    retry: false,
    staleTime: 60 * 60 * 1000
  });

export const useDonationYearSummary = (year: number | undefined) =>
  useQuery({
    queryKey: [...DONATIONS_QUERY_KEY, 'summary', year],
    queryFn: () => getDonationYearSummary(year as number),
    enabled: year !== undefined
  });

export const useCharityYearDetail = (charityId: string, year: number) =>
  useQuery({
    queryKey: [...DONATIONS_QUERY_KEY, 'charity', charityId, year],
    queryFn: () => getCharityYearDetail(charityId, year)
  });

export const useCharityDonations = (
  charityId: string,
  year: number,
  search: string
) =>
  useInfiniteQuery({
    queryKey: [
      ...DONATIONS_QUERY_KEY,
      'charity',
      charityId,
      year,
      'list',
      search
    ],
    queryFn: ({ pageParam }) =>
      getCharityDonations(charityId, year, pageParam, search),
    initialPageParam: 1,
    getNextPageParam: (last) => last.next_page ?? undefined
  });

export const useDonorTaxProfile = (enabled = true) =>
  useQuery({
    queryKey: DONOR_TAX_PROFILE_QUERY_KEY,
    queryFn: getDonorTaxProfile,
    enabled
  });

export const useSaveIcNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveIcNumberPayload) => saveDonorIcNumber(payload),
    onSuccess: (profile: DonorTaxProfile) => {
      queryClient.setQueryData(DONOR_TAX_PROFILE_QUERY_KEY, profile);
    },
    onError: (error: unknown) => {
      const code = apiErrorCode(error);
      if (code && STALE_PROFILE_CODES.has(code)) {
        void queryClient.invalidateQueries({
          queryKey: DONOR_TAX_PROFILE_QUERY_KEY
        });
      }
    }
  });
};
